import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Avatar, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, IconButton, ListItemAvatar, ListItemButton, ListItemText,
  Popover, TextField, Tooltip, Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/CheckOutlined';
import LogoutIcon from '@mui/icons-material/LogoutOutlined';
import CookieIcon from '@mui/icons-material/CookieOutlined';
import PersonIcon from '@mui/icons-material/PersonOutlined';
import BusinessIcon from '@mui/icons-material/BusinessOutlined';
import LinkIcon from '@mui/icons-material/LinkOutlined';
import LinkOffIcon from '@mui/icons-material/LinkOffOutlined';
import SwapHorizIcon from '@mui/icons-material/SwapHorizOutlined';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOverOutlined';

import { reopenConsentBanner } from '../../../shared/components/ConsentBanner';
import { getApiErrorMessage } from '../../../shared/api/errors';
import { useAuth } from '../contexts/AuthenticationContext';
import { useProfile, useLinkedAccounts, useAddLinkedAccount, useRemoveLinkedAccount, useIsSystemAdmin, LinkedAccount } from '../hooks';
import { useActiveContext, ActiveContext, PlatformView } from '../contexts/ActiveOrgContext';
import { displayNameFor, looksLikePlaceholderEmail, canActAsInstructor } from '../helpers';
import { initialsForUser } from '../../../shared/utils/dates';
import { colorFor } from '../../../shared/utils/colors';

function contextKey(c: ActiveContext): string {
  return c.kind === 'personal' ? 'personal' : `org:${c.orgId}`;
}

function contextLabel(c: ActiveContext): string {
  return c.kind === 'personal' ? 'Personal' : c.name;
}

interface Props {
  trigger?: React.ReactElement;
  placement?: 'bottom-end' | 'top-start';
}

export default function AccountSwitcher({ trigger, placement = 'bottom-end' }: Props) {
  const navigate = useNavigate();
  const { user, logout, switchToAccount } = useAuth();
  const { data: profile } = useProfile();
  const { active, available, switchTo, platformView, setPlatformView } = useActiveContext();
  const isPlatformAdmin = useIsSystemAdmin();
  const { data: linkedAccounts = [] } = useLinkedAccounts();
  const addLinkedAccount = useAddLinkedAccount();
  const removeLinkedAccount = useRemoveLinkedAccount();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [addError, setAddError] = useState<string | null>(null);

  const open = Boolean(anchorEl);
  const handleClose = () => setAnchorEl(null);

  const displayName = displayNameFor(profile, user);
  const rawEmail = profile?.email_address || user?.email || '';
  const email = looksLikePlaceholderEmail(rawEmail) ? '' : rawEmail;
  const personalKey = email || displayName || 'personal';

  // Only users who are BOTH a system admin and hold an instructor record get
  // the admin↔instructor switch. Default view is admin until they pick one.
  const dualPlatformRole = isPlatformAdmin && canActAsInstructor(profile, user);
  const platformViewEffective: PlatformView = platformView ?? 'admin';

  const handleSwitchRole = (view: PlatformView) => {
    setPlatformView(view);
    // Platform views are personal-scoped — leaving an org context first means
    // the sidebar surfaces the chosen platform section rather than org links.
    if (active.kind !== 'personal') switchTo({ kind: 'personal' });
    navigate(view === 'admin' ? '/admin/dashboard' : '/instructor/events');
    handleClose();
  };

  const openAddDialog = () => {
    setNewEmail('');
    setAddError(null);
    setAddDialogOpen(true);
  };
  const closeAddDialog = () => {
    setAddDialogOpen(false);
    setAddError(null);
  };
  const submitAdd = () => {
    const trimmed = newEmail.trim();
    if (!trimmed) {
      setAddError('Enter an email address');
      return;
    }
    addLinkedAccount.mutate(trimmed, {
      onSuccess: () => closeAddDialog(),
      onError: (err) => {
        setAddError(getApiErrorMessage(err, 'Could not link that account'));
      },
    });
  };

  const handleSwitchToLinked = (acct: LinkedAccount) => {
    handleClose();
    switchToAccount(acct.email_address);
  };

  const triggerButton = trigger
    ? React.cloneElement(trigger as React.ReactElement<any>, {
        onClick: (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget),
        'aria-label': 'Switch account',
      })
    : (
      <IconButton
        onClick={(e) => setAnchorEl(e.currentTarget)}
        aria-label="Switch account"
        sx={{ p: 0.5 }}
      >
        <Avatar sx={{ width: 32, height: 32, bgcolor: colorFor(personalKey), fontSize: 13, fontWeight: 600 }}>
          {initialsForUser({ name: displayName, email })}
        </Avatar>
      </IconButton>
    );

  return (
    <>
      {triggerButton}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={placement === 'bottom-end'
          ? { vertical: 'bottom', horizontal: 'right' }
          : { vertical: 'top', horizontal: 'right' }}
        transformOrigin={placement === 'bottom-end'
          ? { vertical: 'top', horizontal: 'right' }
          : { vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { width: 320, mt: placement === 'bottom-end' ? 0.5 : -1, ml: placement === 'bottom-end' ? 0 : 1 } } }}
      >
        <Box sx={{ p: 2 }}>
          <Typography sx={{ fontSize: 13, color: 'text.primary', fontWeight: 500, textAlign: 'center' }}>
            {email}
          </Typography>
          <Typography sx={{ fontSize: 11, color: 'text.secondary', textAlign: 'center', mt: 0.25 }}>
            {active.kind === 'org' ? `Acting as ${active.name} (${active.role})` : 'Personal account'}
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5, mb: 1 }}>
            <Avatar
              sx={{ width: 64, height: 64, bgcolor: colorFor(personalKey), fontSize: 24, fontWeight: 600 }}
            >
              {initialsForUser({ name: displayName, email })}
            </Avatar>
          </Box>

          <Typography sx={{ fontSize: 18, fontWeight: 500, textAlign: 'center' }}>
            Hi, {(displayName || '').split(' ')[0]}!
          </Typography>
        </Box>

        <Divider />

        <Box sx={{ py: 0.5 }}>
          <Typography sx={{ fontSize: 11, color: 'text.secondary', textTransform: 'uppercase', px: 2, pt: 1, letterSpacing: 0.5 }}>
            Switch account
          </Typography>
          {available.map((ctx) => {
            const selected = contextKey(ctx) === contextKey(active);
            const key = ctx.kind === 'personal' ? personalKey : ctx.slug;
            return (
              <ListItemButton
                key={contextKey(ctx)}
                onClick={() => { switchTo(ctx); handleClose(); }}
                sx={{ px: 2, py: 1 }}
              >
                <ListItemAvatar sx={{ minWidth: 44 }}>
                  <Avatar sx={{ width: 32, height: 32, bgcolor: colorFor(key), fontSize: 13 }}>
                    {ctx.kind === 'personal'
                      ? <PersonIcon sx={{ fontSize: 18 }} />
                      : <BusinessIcon sx={{ fontSize: 18 }} />}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={contextLabel(ctx)}
                  secondary={ctx.kind === 'personal' ? email : `${ctx.role} · ${ctx.slug}`}
                  slotProps={{
                    primary: { sx: { fontSize: 14, fontWeight: 500 } },
                    secondary: { sx: { fontSize: 12 } },
                  }}
                />
                {selected && (
                  <Chip
                    icon={<CheckIcon sx={{ fontSize: 14 }} />}
                    label="Active"
                    size="small"
                    color="primary"
                    sx={{ fontSize: 11, height: 22 }}
                  />
                )}
              </ListItemButton>
            );
          })}
        </Box>

        {dualPlatformRole && (
          <>
            <Divider />
            <Box sx={{ py: 0.5 }}>
              <Typography sx={{ fontSize: 11, color: 'text.secondary', textTransform: 'uppercase', px: 2, pt: 1, letterSpacing: 0.5 }}>
                View as
              </Typography>
              {([
                { view: 'admin' as const, label: 'Admin', caption: 'System administration', icon: <AdminPanelSettingsIcon sx={{ fontSize: 18 }} /> },
                { view: 'instructor' as const, label: 'Instructor', caption: 'Your teaching tools', icon: <RecordVoiceOverIcon sx={{ fontSize: 18 }} /> },
              ]).map((opt) => {
                const selected = active.kind === 'personal' && platformViewEffective === opt.view;
                return (
                  <ListItemButton
                    key={opt.view}
                    onClick={() => handleSwitchRole(opt.view)}
                    sx={{ px: 2, py: 1 }}
                  >
                    <ListItemAvatar sx={{ minWidth: 44 }}>
                      <Avatar sx={{ width: 32, height: 32, bgcolor: colorFor(`role:${opt.view}`), fontSize: 13 }}>
                        {opt.icon}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={opt.label}
                      secondary={opt.caption}
                      slotProps={{
                        primary: { sx: { fontSize: 14, fontWeight: 500 } },
                        secondary: { sx: { fontSize: 12 } },
                      }}
                    />
                    {selected && (
                      <Chip
                        icon={<CheckIcon sx={{ fontSize: 14 }} />}
                        label="Active"
                        size="small"
                        color="primary"
                        sx={{ fontSize: 11, height: 22 }}
                      />
                    )}
                  </ListItemButton>
                );
              })}
            </Box>
          </>
        )}

        <Divider />

        <Box sx={{ py: 0.5 }}>
          <Typography sx={{ fontSize: 11, color: 'text.secondary', textTransform: 'uppercase', px: 2, pt: 1, letterSpacing: 0.5 }}>
            Linked accounts
          </Typography>

          {linkedAccounts.length === 0 && (
            <Typography sx={{ fontSize: 12, color: 'text.secondary', px: 2, pt: 0.5, pb: 0.5 }}>
              No linked accounts. Add one to switch between them quickly.
            </Typography>
          )}

          {linkedAccounts.map((acct) => (
            <ListItemButton
              key={acct.id}
              onClick={() => handleSwitchToLinked(acct)}
              sx={{ px: 2, py: 1 }}
            >
              <ListItemAvatar sx={{ minWidth: 44 }}>
                <Avatar sx={{ width: 32, height: 32, bgcolor: colorFor(acct.email_address), fontSize: 13, fontWeight: 600 }}>
                  {initialsForUser({ full_name: acct.full_name, email_address: acct.email_address })}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={acct.full_name || acct.email_address}
                secondary={acct.full_name ? acct.email_address : 'Click to sign in'}
                slotProps={{
                  primary: { sx: { fontSize: 13, fontWeight: 500 } },
                  secondary: { sx: { fontSize: 11, wordBreak: 'break-word' } },
                }}
              />
              <Tooltip title={`Switch to ${acct.email_address}`}>
                <SwapHorizIcon sx={{ fontSize: 18, color: 'text.secondary', mr: 0.5 }} />
              </Tooltip>
              <Tooltip title="Remove linked account">
                <span>
                  <IconButton
                    size="small"
                    disabled={removeLinkedAccount.isPending}
                    onClick={(e) => {
                      e.stopPropagation();
                      removeLinkedAccount.mutate(acct.id);
                    }}
                    aria-label={`Remove ${acct.email_address}`}
                  >
                    <LinkOffIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </span>
              </Tooltip>
            </ListItemButton>
          ))}

          <Box sx={{ px: 2, pt: 0.5, pb: 1 }}>
            <Button
              size="small"
              startIcon={<LinkIcon />}
              onClick={openAddDialog}
              sx={{ textTransform: 'none', fontSize: 13 }}
            >
              Link another account
            </Button>
          </Box>
        </Box>

        <Divider />

        <Box sx={{ p: 1 }}>
          <Button
            fullWidth
            startIcon={<PersonIcon />}
            onClick={() => {
              navigate('/profile');
              handleClose();
            }}
            sx={{ justifyContent: 'flex-start', color: 'text.primary', textTransform: 'none' }}
          >
            Edit profile
          </Button>
          {/* Withdrawing consent must be as easy as giving it, and the app has
              no footer to hang this on. */}
          <Button
            fullWidth
            startIcon={<CookieIcon />}
            onClick={() => { reopenConsentBanner(); handleClose(); }}
            sx={{ justifyContent: 'flex-start', color: 'text.primary', textTransform: 'none' }}
          >
            Cookie settings
          </Button>
          <Button
            fullWidth startIcon={<LogoutIcon />} onClick={() => { logout(); handleClose(); }}
            sx={{ justifyContent: 'flex-start', color: 'text.primary', textTransform: 'none' }}
          >
            Sign out
          </Button>
        </Box>
      </Popover>

      <Dialog open={addDialogOpen} onClose={closeAddDialog} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 16, fontWeight: 600 }}>Link another account</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: 'text.primary', mb: 2 }}>
            Enter the email of another account you own. We'll show it in your dropdown so
            you can switch between them quickly. The account must already exist (i.e.
            have signed in at least once).
          </Typography>
          <TextField
            autoFocus
            fullWidth
            type="email"
            label="Email address"
            value={newEmail}
            onChange={(e) => { setNewEmail(e.target.value); setAddError(null); }}
            onKeyDown={(e) => { if (e.key === 'Enter') submitAdd(); }}
            error={!!addError}
            helperText={addError || ' '}
            disabled={addLinkedAccount.isPending}
            slotProps={{
              htmlInput: { autoComplete: 'email' },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeAddDialog} disabled={addLinkedAccount.isPending}>Cancel</Button>
          <Button
            variant="contained"
            onClick={submitAdd}
            disabled={addLinkedAccount.isPending}
            startIcon={addLinkedAccount.isPending ? <CircularProgress size={14} /> : <LinkIcon />}
          >
            Link
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
